/**
 * Where each history entry was left scrolled, so Back and Forward return
 * there. ScrollManager (components/layout) records and restores; pages ask
 * isHistoryRestore() before running their own arrival scroll.
 *
 * Why (owner report 2026-10-02): the router keeps the window's scroll across
 * in-app navigations, so a footer link at the bottom of the landing page
 * opened Privacy, Terms, Pricing, the editor... at their bottom too, and the
 * browser's own restoration ('auto') raced the landing page's scroll-to-top,
 * so Back landed at the top one time and in place the next.
 */
import type { Location } from 'react-router-dom';

const STORAGE_KEY = 'fs-scroll-positions';
const MAX_ENTRIES = 160; // an entry and its URL copy (urlKey) each

type EntryLocation = Pick<Location, 'key' | 'pathname' | 'search' | 'hash'>;

// React Router gives an entry with no history state (the first page of a
// document: a typed URL, a plain <a href> to another page, a plain
// <a href="#id">) the key "default". Keyed by URL, two such entries of one
// page (/pricing opened, then /pricing again from a plain link) shared one
// position, the later visit's (review 2026-10-03). So every such entry gets a
// key of its own in its history state (stampEntry), as the router's own
// entries have: a reload and Back from another site find it there again.
export function entryKey(loc: EntryLocation): string {
  if (loc.key && loc.key !== 'default') return loc.key;
  return stateKey() || `default:${loc.pathname}${loc.search}${loc.hash}`;
}

function stateKey(): string | null {
  try {
    const key = (window.history.state as { key?: unknown } | null)?.key;
    return typeof key === 'string' && key ? key : null;
  } catch {
    return null;
  }
}

let stamped: string | null = null;

/** Gives the entry on screen a key when it has none; its key either way. */
export function stampEntry(): string | null {
  const held = stateKey();
  if (held) return held;
  try {
    const key = Math.random().toString(36).slice(2, 10);
    const state = window.history.state;
    window.history.replaceState({ ...(state && typeof state === 'object' ? state : {}), key }, '');
    stamped = key;
    return key;
  } catch {
    return null;
  }
}

/** True when `key` is one stampEntry gave an entry that came up without one. */
export function wasStamped(key: string): boolean {
  return stamped === key;
}

// Every position is also kept under its URL. A reload can bring an entry
// back WITHOUT its history state: Chrome's reload of a crashed tab (the "Aw,
// Snap!" Reload) drops it, so the entry is stamped a new key and what was
// kept under the old one is never read (review 2026-10-03). Such a load
// falls back to the URL's copy: the last position this tab left that URL at.
export function urlKey(loc: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  return `url:${loc.pathname}${loc.search}${loc.hash}`;
}

/**
 * Where an entry was left: `y`, and the page's SIGNATURE then (pageSignature):
 * a page whose layout depends on state the URL does not carry says which
 * layout it was in. The landing page does (its compass stop, HeroSection.tsx:
 * a remount reopens it on its welcome stop, and the stops differ in height
 * by more than a thousand pixels), so a Y saved on one stop is never put back
 * onto another (2026-10-03 review).
 */
export interface SavedPosition { y: number; sig: string }

const positions = new Map<string, SavedPosition>();
let loaded = false;

/** The page's signature: the data-scroll-signature on screen ('' when none says). */
export function pageSignature(): string {
  try {
    return document.querySelector('[data-scroll-signature]')?.getAttribute('data-scroll-signature') || '';
  } catch {
    return '';
  }
}

// Kept in sessionStorage over a full page load: Back from Stripe Checkout or
// another site, and a reload (or a phone reloading a tab it put away).
function load() {
  if (loaded) return;
  loaded = true;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : null;
    if (!Array.isArray(list)) return;
    for (const item of list) {
      if (!Array.isArray(item) || typeof item[0] !== 'string' || typeof item[1] !== 'number') continue;
      positions.set(item[0], { y: item[1], sig: typeof item[2] === 'string' ? item[2] : '' });
    }
  } catch { /* storage blocked or corrupt: start empty */ }
}

export function savePosition(key: string, y: number, sig = '') {
  load();
  positions.delete(key);
  positions.set(key, { y: Math.max(0, Math.round(y)), sig });
  if (positions.size > MAX_ENTRIES) positions.delete(positions.keys().next().value as string);
}

export function savedPosition(key: string): SavedPosition | undefined {
  load();
  return positions.get(key);
}

export function persistPositions() {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...positions].map(([k, p]) => [k, p.y, p.sig])));
  } catch { /* storage blocked or full: Back after a full reload starts at the top */ }
}

/** Router state for a navigation that is the same page in another form (the
 *  editor's slug in the other language): it keeps the scroll where it is. */
export const KEEP_SCROLL = { keepScroll: true } as const;

export function keepsScroll(state: unknown): boolean {
  return !!state && typeof state === 'object' && (state as { keepScroll?: unknown }).keepScroll === true;
}

let restoring: string | null = null;

export function setRestoring(key: string | null) {
  restoring = key;
}

/** True while Back/Forward brought this entry up and ScrollManager is putting
 *  it where it was left: the page skips its own arrival scroll (top, #hash). */
export function isHistoryRestore(loc: EntryLocation): boolean {
  return restoring !== null && restoring === entryKey(loc);
}

/** Tests only. */
export function resetScrollMemory() {
  positions.clear();
  loaded = true;
  restoring = null;
  stamped = null;
}
